import { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import "styles/dashboard.css";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, ReferenceLine,
} from "recharts";

// ─────────────────────────────────────────────────────────────────────────────
// STATIC MOCK DATA
// ─────────────────────────────────────────────────────────────────────────────

const AUM = 24_700_000;

const CLASS_INFO = {
  EQ: { label: "Equity",       color: "#2b36d9" },
  FI: { label: "Fixed Income", color: "#f59e0b" },
  CD: { label: "Commodity",    color: "#04ff1d" },
  MF: { label: "Alternative",  color: "#a084c9" },
  CS: { label: "Cash",         color: "#939393" },
};

const INITIAL_POSITIONS = [
  { id:1,  ticker:"AAPL",  name:"Apple Inc.",              assetClass:"EQ", currentWeight:12.5, targetWeight:12.5, varContrib:9.2,  locked:false },
  { id:2,  ticker:"MSFT",  name:"Microsoft Corp.",         assetClass:"EQ", currentWeight:10.8, targetWeight:10.8, varContrib:8.1,  locked:false },
  { id:3,  ticker:"NVDA",  name:"NVIDIA Corp.",            assetClass:"EQ", currentWeight:8.2,  targetWeight:8.2,  varContrib:11.3, locked:false },
  { id:4,  ticker:"GOOGL", name:"Alphabet Inc.",           assetClass:"EQ", currentWeight:7.5,  targetWeight:7.5,  varContrib:6.8,  locked:false },
  { id:5,  ticker:"AMZN",  name:"Amazon.com Inc.",         assetClass:"EQ", currentWeight:6.9,  targetWeight:6.9,  varContrib:7.2,  locked:false },
  { id:6,  ticker:"BRK.B", name:"Berkshire Hathaway B",    assetClass:"EQ", currentWeight:4.2,  targetWeight:4.2,  varContrib:3.1,  locked:false },
  { id:7,  ticker:"LQD",   name:"iShares Corp Bond ETF",   assetClass:"FI", currentWeight:8.5,  targetWeight:8.5,  varContrib:2.8,  locked:false },
  { id:8,  ticker:"TLT",   name:"iShares 20+ Yr Treasury", assetClass:"FI", currentWeight:6.3,  targetWeight:6.3,  varContrib:3.5,  locked:false },
  { id:9,  ticker:"HYG",   name:"iShares High Yield Corp", assetClass:"FI", currentWeight:5.1,  targetWeight:5.1,  varContrib:4.9,  locked:false },
  { id:10, ticker:"GLD",   name:"SPDR Gold Shares",        assetClass:"CD", currentWeight:4.8,  targetWeight:4.8,  varContrib:3.8,  locked:false },
  { id:11, ticker:"DBC",   name:"Invesco DB Commodity",    assetClass:"CD", currentWeight:3.2,  targetWeight:3.2,  varContrib:4.2,  locked:false },
  { id:12, ticker:"REET",  name:"iShares Global REIT",     assetClass:"MF", currentWeight:3.8,  targetWeight:3.8,  varContrib:5.6,  locked:false },
  { id:13, ticker:"QAI",   name:"IQ Hedge Multi-Strat",    assetClass:"MF", currentWeight:2.9,  targetWeight:2.9,  varContrib:2.4,  locked:false },
  { id:14, ticker:"CASH",  name:"Cash & Equivalents",      assetClass:"CS", currentWeight:15.3, targetWeight:15.3, varContrib:0.0,  locked:true  },
];

const BMK = {
  AAPL:10.0, MSFT:9.0, NVDA:6.0, GOOGL:7.0, AMZN:6.0, "BRK.B":5.0,
  LQD:9.0, TLT:8.0, HYG:5.0, GLD:5.0, DBC:3.0, REET:4.0, QAI:3.0, CASH:20.0,
};

const BASE_METRICS = {
  varPct: 1.42, varLimit: 2.0, varUtilization: 0.71,
  volatility: 11.8, beta: 0.92, sharpe: 1.34,
};

const BASE_VAR_SCORE = INITIAL_POSITIONS.reduce((s, p) => s + p.currentWeight * p.varContrib, 0);

const CONSTRAINT_RULES = [
  { label:"Max Position (15%)",    check:(pos)    => pos.filter(p => p.targetWeight > 15).map(p => `${p.ticker} at ${p.targetWeight.toFixed(1)}%`) },
  { label:"Max Equity (65%)",      check:(pos)    => { const v = pos.filter(p=>p.assetClass==="EQ").reduce((s,p)=>s+p.targetWeight,0); return v>65?[`Equity ${v.toFixed(1)}% > 65%`]:[]; } },
  { label:"Max Fixed Income (40%)",check:(pos)    => { const v = pos.filter(p=>p.assetClass==="FI").reduce((s,p)=>s+p.targetWeight,0); return v>40?[`Fixed Income ${v.toFixed(1)}% > 40%`]:[]; } },
  { label:"VaR Limit (2.0%)",      check:(_,sim)  => sim.varPct > sim.varLimit ? [`VaR ${sim.varPct.toFixed(2)}% > ${sim.varLimit.toFixed(1)}% limit`] : [] },
  { label:"Min Cash (2%)",         check:(pos)    => { const v = pos.filter(p=>p.assetClass==="CS").reduce((s,p)=>s+p.targetWeight,0); return v<2?[`Cash ${v.toFixed(1)}% < 2% min`]:[]; } },
];

const INITIAL_SCENARIOS = [
  {
    id:1, name:"Reduce Tech Exposure", createdAt:"2026-03-15",
    positions: INITIAL_POSITIONS.map(p => {
      const adj = { AAPL:-3.0, NVDA:-2.5, LQD:2.5, TLT:2.0, CASH:1.0 };
      return { ...p, targetWeight: Math.max(0, p.currentWeight + (adj[p.ticker]||0)) };
    }),
  },
  {
    id:2, name:"Snap to Benchmark", createdAt:"2026-03-20",
    positions: INITIAL_POSITIONS.map(p => ({ ...p, targetWeight: BMK[p.ticker] ?? p.currentWeight })),
  },
  {
    id:3, name:"Defensive Tilt", createdAt:"2026-03-28",
    positions: INITIAL_POSITIONS.map(p => {
      const adj = { AAPL:-5.0, MSFT:-3.0, NVDA:-4.0, GLD:3.0, TLT:4.0, CASH:5.0 };
      return { ...p, targetWeight: Math.max(0, p.currentWeight + (adj[p.ticker]||0)) };
    }),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const fmtK = (n) => {
  if (n == null) return "—";
  const abs = Math.abs(n);
  return abs >= 1e6 ? `$${(abs/1e6).toFixed(2)}M` : `$${(abs/1e3).toFixed(0)}K`;
};

function computeMetrics(positions) {
  const varScore = positions.reduce((s, p) => s + p.targetWeight * p.varContrib, 0);
  const ratio    = BASE_VAR_SCORE > 0 ? varScore / BASE_VAR_SCORE : 1;
  const varPct   = +(BASE_METRICS.varPct * ratio).toFixed(2);
  return {
    varPct,
    varUtilization: varPct / BASE_METRICS.varLimit,
    varLimit:       BASE_METRICS.varLimit,
    volatility:     +(BASE_METRICS.volatility * Math.sqrt(ratio)).toFixed(1),
    beta:           +(BASE_METRICS.beta * ratio).toFixed(2),
    sharpe:         +(BASE_METRICS.sharpe / Math.sqrt(ratio)).toFixed(2),
  };
}

function computeSensitivityCurve(positions, ticker) {
  const pos = positions.find(p => p.ticker === ticker);
  if (!pos) return [];
  const unlocked      = positions.filter(p => p.ticker !== ticker && !p.locked);
  const unlockedTotal = unlocked.reduce((s, p) => s + p.targetWeight, 0);
  return [-12,-10,-8,-6,-4,-2,0,2,4,6,8,10,12].map(delta => {
    const newW = pos.targetWeight + delta;
    if (newW < 0 || newW > 100) return null;
    const adjusted = positions.map(p => {
      if (p.ticker === ticker) return { ...p, targetWeight: newW };
      if (p.locked) return p;
      const share = unlockedTotal > 0 ? p.targetWeight / unlockedTotal : 0;
      return { ...p, targetWeight: Math.max(0, p.targetWeight - delta * share) };
    });
    const m = computeMetrics(adjusted);
    return { delta, varPct: m.varPct, volatility: m.volatility, sharpe: m.sharpe };
  }).filter(Boolean);
}

function normalizeWeights(positions) {
  const lockedSum    = positions.filter(p => p.locked).reduce((s, p) => s + p.targetWeight, 0);
  const unlockedSum  = positions.filter(p => !p.locked).reduce((s, p) => s + p.targetWeight, 0);
  const target       = 100 - lockedSum;
  if (unlockedSum === 0) return positions;
  const scale = target / unlockedSum;
  return positions.map(p => p.locked ? p : { ...p, targetWeight: +(p.targetWeight * scale).toFixed(2) });
}

function exportTradesCSV(trades, bps) {
  const rows = [["Ticker","Name","Asset Class","Current %","Target %","Trade %","Trade Value ($)","Est. Cost ($)"]];
  trades.forEach(t => {
    const diff = t.targetWeight - t.currentWeight;
    const val  = Math.abs(diff) * AUM / 100;
    rows.push([t.ticker, t.name, CLASS_INFO[t.assetClass]?.label??t.assetClass,
      t.currentWeight.toFixed(2), t.targetWeight.toFixed(2), diff.toFixed(2),
      Math.round(val), Math.round(val * bps / 10000)]);
  });
  const csv  = rows.map(r => r.join(",")).join("\n");
  const url  = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
  Object.assign(document.createElement("a"), { href:url, download:"whatif_trades.csv" }).click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function VaRGauge({ utilization, limitPct, label = "VaR Limit Utilization" }) {
  const r = 60, cx = 110, cy = 72, sw = 13;
  const u = Math.min(Math.max(utilization || 0, 0), 1);
  const color       = u < 0.5 ? "#04ff1d" : u < 0.75 ? "#f59e0b" : "#ff2e2e";
  const statusLabel = u >= 0.75 ? "ELEVATED" : u >= 0.5 ? "MODERATE" : "LOW";
  const trackPath   = `M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`;
  const angle       = Math.PI * (1 - u);
  const fX = (cx + r * Math.cos(angle)).toFixed(2);
  const fY = (cy - r * Math.sin(angle)).toFixed(2);
  const fillPath = u > 0.005 ? `M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${fX} ${fY}` : null;
  const ndX = (cx + r * 0.82 * Math.cos(angle)).toFixed(2);
  const ndY = (cy - r * 0.82 * Math.sin(angle)).toFixed(2);
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 12px 14px", display:"flex", flexDirection:"column", alignItems:"center" }}>
      <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:8, alignSelf:"flex-start" }}>{label}</div>
      <svg width="100%" viewBox="0 0 220 110">
        <path d={trackPath} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={sw} strokeLinecap="round" />
        {fillPath && <path d={fillPath} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" />}
        <line x1={cx} y1={cy} x2={ndX} y2={ndY} stroke="rgba(255,255,255,0.75)" strokeWidth={2.5} strokeLinecap="round" />
        <circle cx={cx} cy={cy} r={5} fill="rgba(255,255,255,0.75)" />
        <circle cx={cx} cy={cy} r={2.5} fill="#0d0f1a" />
        <text x={cx-r-4} y={cy+16} textAnchor="end"   fill="rgba(255,255,255,0.25)" fontSize={9}>0%</text>
        <text x={cx+r+4} y={cy+16} textAnchor="start" fill="rgba(255,255,255,0.25)" fontSize={9}>{limitPct?.toFixed(0)}%</text>
        <text x={cx} y={cy+26} textAnchor="middle" fill={color} fontSize={22} fontWeight="700" fontFamily="monospace">{(u*100).toFixed(0)}%</text>
        <text x={cx} y={cy+39} textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize={9}>utilization of limit</text>
      </svg>
      <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", textAlign:"center", marginBottom:10 }}>1d VaR 99% · Limit: {limitPct?.toFixed(1)}% AUM</div>
      <div style={{ padding:"4px 14px", borderRadius:4, fontSize:11, fontWeight:700, letterSpacing:"0.5px",
        background: u>=0.75?"rgba(255,46,46,0.15)":u>=0.5?"rgba(245,158,11,0.15)":"rgba(4,255,29,0.10)", color }}>
        {statusLabel} RISK
      </div>
    </div>
  );
}

function MetricDeltaCard({ label, before, after, unit = "%", accentColor, i = 0, higherIsBetter = false }) {
  const delta    = after - before;
  const improved = higherIsBetter ? delta > 0.005  : delta < -0.005;
  const worsened = higherIsBetter ? delta < -0.005 : delta > 0.005;
  const deltaColor = improved ? "#04ff1d" : worsened ? "#ff2e2e" : "var(--gray_500)";
  const sign = delta >= 0 ? "+" : "";
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"16px", position:"relative", animation:`fadeSlideUp 0.4s ${0.05+i*0.05}s ease both` }}>
      {accentColor && <div style={{ position:"absolute", top:0, left:0, right:0, height:2, borderRadius:"10px 10px 0 0", background:accentColor }} />}
      <div style={{ fontSize:9, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:8 }}>{label}</div>
      <div style={{ display:"flex", alignItems:"baseline", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontSize:13, fontFamily:"monospace", color:"var(--gray_400)", textDecoration:"line-through", opacity:0.65 }}>{before.toFixed(2)}{unit}</span>
        <span style={{ fontSize:9, color:"var(--gray_500)" }}>→</span>
        <span style={{ fontSize:22, fontWeight:700, fontFamily:"monospace", color:"var(--white_a700)" }}>{after.toFixed(2)}{unit}</span>
      </div>
      <div style={{ marginTop:5, fontSize:11, fontFamily:"monospace", color:deltaColor }}>
        {sign}{delta.toFixed(2)}{unit} {improved ? "(improved)" : worsened ? "(worsened)" : "(no change)"}
      </div>
    </div>
  );
}

function AllocationDonut({ data, title }) {
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"16px 20px" }}>
      <div style={{ fontSize:12, fontWeight:600, color:"var(--white_a700)", marginBottom:14 }}>{title}</div>
      <div style={{ display:"flex", alignItems:"center", gap:16 }}>
        <ResponsiveContainer width={110} height={110}>
          <PieChart>
            <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={28} outerRadius={52} paddingAngle={2} startAngle={90} endAngle={-270}>
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
          {data.map((d, i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:8, height:8, borderRadius:2, background:d.color, flexShrink:0 }} />
                <span style={{ fontSize:11, color:"var(--gray_400)" }}>{d.name}</span>
              </div>
              <span style={{ fontSize:12, fontFamily:"monospace", color:"var(--white_a700)" }}>{d.value.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SensTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"8px 12px" }}>
      <div style={{ fontSize:10, color:"var(--gray_500)", marginBottom:3 }}>Δ Weight: {label >= 0 ? "+" : ""}{label}%</div>
      <div style={{ fontSize:13, fontFamily:"monospace", color:payload[0]?.stroke, fontWeight:600 }}>
        {payload[0]?.value?.toFixed(2)}{unit}
      </div>
    </div>
  );
}

function DeltaTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value;
  return (
    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:6, padding:"8px 12px" }}>
      <div style={{ fontSize:11, fontWeight:600, color:"var(--white_a700)", marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:12, fontFamily:"monospace", color: v>=0?"#f59e0b":"#04ff1d" }}>
        {v >= 0 ? "+" : ""}{v?.toFixed(2)}%
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id:"workbench", label:"Rebalancing Workbench" },
  { id:"impact",    label:"Risk Impact" },
  { id:"scenarios", label:"Scenarios & Sensitivity" },
];

export default function WhatIfAnalysis() {
  const [positions,     setPositions]     = useState(INITIAL_POSITIONS);
  const [activeTab,     setActiveTab]     = useState("workbench");
  const [savedScenarios, setSaved]        = useState(INITIAL_SCENARIOS);
  const [scenarioName,  setScenarioName]  = useState("");
  const [sensIdx,       setSensIdx]       = useState(0);
  const [minTradeSize,  setMinTradeSize]  = useState(0.25);
  const [estBps,        setEstBps]        = useState(10);
  const [showDiffOnly,  setShowDiffOnly]  = useState(false);

  // ── Weight editing ────────────────────────────────────────────────────────
  function updateWeight(id, raw) {
    const val = parseFloat(raw);
    setPositions(prev => prev.map(p =>
      p.id === id ? { ...p, targetWeight: isNaN(val) ? p.targetWeight : Math.max(0, Math.min(100, val)) } : p
    ));
  }
  function toggleLock(id) {
    setPositions(prev => prev.map(p => p.id === id ? { ...p, locked: !p.locked } : p));
  }
  function handleSnapBenchmark() {
    setPositions(prev => prev.map(p => p.locked ? p : { ...p, targetWeight: BMK[p.ticker] ?? p.currentWeight }));
  }
  function handleNormalize() {
    setPositions(prev => normalizeWeights(prev));
  }
  function handleReset() { setPositions(INITIAL_POSITIONS); }
  function handleLoadScenario(s) { setPositions(s.positions); }
  function handleDeleteScenario(id) { setSaved(prev => prev.filter(s => s.id !== id)); }
  function handleSaveScenario() {
    if (!scenarioName.trim()) return;
    setSaved(prev => [...prev, {
      id: Date.now(), name: scenarioName.trim(),
      createdAt: new Date().toISOString().split("T")[0],
      positions: positions.map(p => ({ ...p })),
    }]);
    setScenarioName("");
  }

  // ── Computed ──────────────────────────────────────────────────────────────
  const totalTarget = useMemo(
    () => +positions.reduce((s, p) => s + p.targetWeight, 0).toFixed(2),
    [positions]
  );
  const simMetrics = useMemo(() => computeMetrics(positions), [positions]);

  const violations = useMemo(() => {
    const msgs = [];
    CONSTRAINT_RULES.forEach(r => r.check(positions, simMetrics).forEach(m => msgs.push({ rule: r.label, msg: m })));
    if (Math.abs(totalTarget - 100) > 0.05)
      msgs.push({ rule:"Total Weight", msg:`Weights sum to ${totalTarget.toFixed(2)}% — must equal 100%` });
    return msgs;
  }, [positions, simMetrics, totalTarget]);

  const trades = useMemo(() =>
    positions
      .map(p => ({ ...p, diff: p.targetWeight - p.currentWeight }))
      .filter(p => Math.abs(p.diff) >= minTradeSize)
      .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff)),
    [positions, minTradeSize]
  );

  const allocationBefore = useMemo(() => {
    const m = {};
    INITIAL_POSITIONS.forEach(p => { m[p.assetClass] = (m[p.assetClass]||0) + p.currentWeight; });
    return Object.entries(m).map(([k,v]) => ({ name: CLASS_INFO[k]?.label??k, value:v, color: CLASS_INFO[k]?.color??"#888" }));
  }, []);

  const allocationAfter = useMemo(() => {
    const m = {};
    positions.forEach(p => { m[p.assetClass] = (m[p.assetClass]||0) + p.targetWeight; });
    return Object.entries(m).map(([k,v]) => ({ name: CLASS_INFO[k]?.label??k, value:v, color: CLASS_INFO[k]?.color??"#888" }));
  }, [positions]);

  const allocationDelta = useMemo(() => {
    const before = {}; const after = {};
    INITIAL_POSITIONS.forEach(p => { before[p.assetClass] = (before[p.assetClass]||0) + p.currentWeight; });
    positions.forEach(p => { after[p.assetClass] = (after[p.assetClass]||0) + p.targetWeight; });
    return Object.keys(CLASS_INFO)
      .map(k => ({ name: CLASS_INFO[k].label, delta: +((after[k]||0)-(before[k]||0)).toFixed(2) }))
      .filter(d => Math.abs(d.delta) > 0.01);
  }, [positions]);

  const varContribChanges = useMemo(() => {
    const afterScore = positions.reduce((s, p) => s + p.targetWeight * p.varContrib, 0);
    return positions.map(p => {
      const before = +(p.currentWeight * p.varContrib / BASE_VAR_SCORE * 100).toFixed(2);
      const after  = afterScore > 0 ? +(p.targetWeight * p.varContrib / afterScore * 100).toFixed(2) : 0;
      return { ticker: p.ticker, assetClass: p.assetClass, before, after, delta: +(after - before).toFixed(2) };
    })
    .filter(v => v.before > 0.1 || v.after > 0.1 || Math.abs(v.delta) > 0.1)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }, [positions]);

  const nonLocked = useMemo(() => positions.filter(p => !p.locked), [positions]);
  const safeIdx   = Math.min(sensIdx, Math.max(0, nonLocked.length - 1));
  const sensTicker = nonLocked[safeIdx]?.ticker ?? "";
  const sensCurve  = useMemo(() => computeSensitivityCurve(positions, sensTicker), [positions, sensTicker]);

  const scenarioComparison = useMemo(() => [
    { name:"Current",   metrics: computeMetrics(INITIAL_POSITIONS.map(p => ({ ...p, targetWeight: p.currentWeight }))) },
    { name:"Simulated", metrics: simMetrics },
    ...savedScenarios.map(s => ({ name: s.name, metrics: computeMetrics(s.positions) })),
  ], [savedScenarios, simMetrics]);

  const displayedPositions = useMemo(
    () => showDiffOnly ? positions.filter(p => Math.abs(p.targetWeight - p.currentWeight) > 0.01) : positions,
    [positions, showDiffOnly]
  );

  const totalTradeCost = useMemo(
    () => trades.reduce((s, t) => s + Math.abs(t.diff) * AUM / 100 * estBps / 10000, 0),
    [trades, estBps]
  );

  // ── Styles ────────────────────────────────────────────────────────────────
  const btn = (primary = false, danger = false) => ({
    padding:"7px 14px", fontSize:12, fontWeight:600, borderRadius:6, cursor:"pointer",
    border: primary ? "none" : "1px solid var(--gray_900)",
    background: primary ? "var(--indigo_a700)" : danger ? "rgba(255,46,46,0.10)" : "var(--gray_900_01)",
    color: primary ? "#fff" : danger ? "#ff2e2e" : "var(--gray_400)",
    letterSpacing:"0.3px", transition:"opacity 0.15s", whiteSpace:"nowrap",
  });

  const secLabel = { fontSize:10, letterSpacing:"3px", textTransform:"uppercase", color:"var(--indigo_a700)", marginBottom:14 };

  const tabBtn = (id) => {
    const active = activeTab === id;
    return {
      padding:"10px 20px", fontSize:13, fontWeight:active?600:400,
      color:active?"var(--white_a700)":"var(--gray_500)",
      border:"none", borderBottom:active?"2px solid var(--indigo_a700)":"2px solid transparent",
      marginBottom:-1, background:"transparent", cursor:"pointer", outline:"none",
      letterSpacing:"0.3px", transition:"color 0.15s",
    };
  };

  const th = (right = false) => ({
    padding:"10px 14px", textAlign:right?"right":"left",
    fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", whiteSpace:"nowrap",
  });

  const td = (right = false, mono = false, color = "var(--gray_400)") => ({
    padding:"10px 14px", textAlign:right?"right":"left",
    fontSize:mono?13:12, fontFamily:mono?"monospace":"inherit", color,
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>What-If Analysis – Tail Risk Global</title>
        <meta name="description" content="Simulate portfolio rebalancing and assess risk impact." />
      </Helmet>

      <div className="trg-shell">
        <DashboardTopbar />

        <DashboardMenuBar activePage="What-If" />

        <main style={{ flex:1, padding:"36px 40px", overflowY:"auto", overflowX:"hidden", background:"#181719", minHeight:0 }}>

            {/* ── Tab bar ── */}
            <div style={{ display:"flex", alignItems:"stretch", borderBottom:"1px solid var(--gray_900)", marginBottom:28 }}>
              {TABS.map(t => (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabBtn(t.id)}>{t.label}</button>
              ))}
              {violations.length > 0 && (
                <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", paddingBottom:4 }}>
                  <div style={{ padding:"3px 10px", borderRadius:4, fontSize:11, fontWeight:700, background:"rgba(255,46,46,0.10)", color:"#ff2e2e" }}>
                    ⚠ {violations.length} violation{violations.length > 1 ? "s" : ""}
                  </div>
                </div>
              )}
            </div>

            {/* ── Page header ── */}
            <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:24 }}>
              <div>
                <div style={{ fontSize:11, color:"var(--gray_500)", letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:4 }}>Portfolio Analysis</div>
                <div style={{ fontSize:26, fontWeight:600, color:"var(--white_a700)", letterSpacing:"0.3px" }}>What-If Analysis</div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ padding:"5px 10px", borderRadius:4, fontSize:10, fontWeight:700, letterSpacing:"1px", background:"rgba(43,54,217,0.15)", color:"var(--indigo_a700)" }}>
                  STATIC PROTOTYPE
                </div>
                <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", padding:"8px 16px", borderRadius:6, fontSize:12, color:"var(--gray_400)" }}>
                  As of Apr 1, 2026
                </div>
                <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", padding:"8px 16px", borderRadius:6, fontSize:12 }}>
                  <span style={{ color:"var(--gray_500)" }}>AUM</span>
                  <span style={{ color:"var(--white_a700)", marginLeft:8, fontFamily:"monospace", fontWeight:700 }}>$24.70M</span>
                </div>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════
                TAB 1 — WORKBENCH
            ═══════════════════════════════════════════════════════════ */}
            {activeTab === "workbench" && (
              <>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:20, marginBottom:28, alignItems:"start" }}>

                  {/* Position editor table */}
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, overflow:"hidden" }}>
                    <div style={{ padding:"14px 16px", borderBottom:"1px solid var(--gray_900)", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      <span style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)", marginRight:4 }}>Position Editor</span>
                      <button style={btn()} onClick={handleSnapBenchmark}>Snap to Benchmark</button>
                      <button style={btn()} onClick={handleNormalize}>Normalize to 100%</button>
                      <button style={btn()} onClick={() => setShowDiffOnly(v => !v)}>
                        {showDiffOnly ? "Show All" : "Changed Only"}
                      </button>
                      <button style={{ ...btn(false, true), marginLeft:"auto" }} onClick={handleReset}>Reset ↺</button>
                    </div>
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead>
                          <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
                            <th style={th()}>Ticker</th>
                            <th style={th()}>Name</th>
                            <th style={th()}>Class</th>
                            <th style={th(true)}>Current %</th>
                            <th style={th(true)}>Target %</th>
                            <th style={th(true)}>Δ Weight</th>
                            <th style={{ ...th(), textAlign:"center" }}>Lock</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedPositions.map((p, i) => {
                            const diff  = p.targetWeight - p.currentWeight;
                            const dClr  = Math.abs(diff) < 0.01 ? "var(--gray_500)" : diff > 0 ? "#f59e0b" : "#04ff1d";
                            return (
                              <tr key={p.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                                <td style={td()}>
                                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                    <div style={{ width:6, height:6, borderRadius:2, background:CLASS_INFO[p.assetClass]?.color??"#888", flexShrink:0 }} />
                                    <span style={{ fontSize:13, fontWeight:600, fontFamily:"monospace", color:"var(--white_a700)" }}>{p.ticker}</span>
                                  </div>
                                </td>
                                <td style={td()}>{p.name}</td>
                                <td style={td()}>
                                  <span style={{ fontSize:10, padding:"2px 7px", borderRadius:4, fontWeight:600,
                                    background:`${CLASS_INFO[p.assetClass]?.color}22`, color:CLASS_INFO[p.assetClass]?.color }}>
                                    {CLASS_INFO[p.assetClass]?.label ?? p.assetClass}
                                  </span>
                                </td>
                                <td style={td(true, true, "var(--gray_400)")}>{p.currentWeight.toFixed(2)}%</td>
                                <td style={{ padding:"8px 14px", textAlign:"right" }}>
                                  {p.locked ? (
                                    <span style={{ fontSize:13, fontFamily:"monospace", color:"var(--gray_500)" }}>{p.targetWeight.toFixed(2)}%</span>
                                  ) : (
                                    <div style={{ display:"flex", alignItems:"center", gap:4, justifyContent:"flex-end" }}>
                                      <input type="number" step="0.1" min="0" max="100"
                                        value={p.targetWeight}
                                        onChange={e => updateWeight(p.id, e.target.value)}
                                        style={{ width:70, padding:"4px 8px", fontSize:13, fontFamily:"monospace", textAlign:"right",
                                          background:"var(--gray_900_02)", border:"1px solid var(--gray_900)", borderRadius:5,
                                          color:"var(--white_a700)", outline:"none" }} />
                                      <span style={{ fontSize:11, color:"var(--gray_500)" }}>%</span>
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding:"10px 14px", textAlign:"right", fontSize:13, fontFamily:"monospace", color:dClr, fontWeight:Math.abs(diff)>0.01?600:400 }}>
                                  {Math.abs(diff) < 0.01 ? "—" : `${diff>0?"+":""}${diff.toFixed(2)}%`}
                                </td>
                                <td style={{ padding:"10px 14px", textAlign:"center" }}>
                                  <button onClick={() => toggleLock(p.id)} title={p.locked?"Unlock":"Lock"} style={{ background:"transparent", border:"1px solid var(--gray_900)", borderRadius:4, padding:"3px 8px", cursor:"pointer", fontSize:13, color:p.locked?"#f59e0b":"var(--gray_500)" }}>
                                    {p.locked ? "🔒" : "🔓"}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                          {displayedPositions.length === 0 && (
                            <tr><td colSpan={7} style={{ padding:"28px", textAlign:"center", fontSize:13, color:"var(--gray_500)" }}>No changed positions</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Right panel */}
                  <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

                    {/* Total weight */}
                    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 20px" }}>
                      <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:12 }}>Portfolio Weights</div>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                        <span style={{ fontSize:12, color:"var(--gray_400)" }}>Target total</span>
                        <span style={{ fontSize:16, fontFamily:"monospace", fontWeight:700, color:Math.abs(totalTarget-100)>0.05?"#ff2e2e":"#04ff1d" }}>
                          {totalTarget.toFixed(2)}%
                        </span>
                      </div>
                      <div style={{ height:6, background:"var(--gray_900)", borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${Math.min(totalTarget,100)}%`, background:Math.abs(totalTarget-100)>0.05?"#ff2e2e":"#04ff1d", borderRadius:3, transition:"width 0.3s ease" }} />
                      </div>
                      <div style={{ marginTop:8, fontSize:10, color:Math.abs(totalTarget-100)>0.05?"#ff2e2e":"var(--gray_500)" }}>
                        {Math.abs(totalTarget-100) < 0.05 ? "✓ Balanced" : `${totalTarget>100?"Over":"Under"} by ${Math.abs(totalTarget-100).toFixed(2)}%`}
                      </div>
                    </div>

                    {/* Constraint checker */}
                    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 20px" }}>
                      <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:12 }}>Constraint Check</div>
                      {violations.length === 0 ? (
                        <div style={{ fontSize:12, color:"#04ff1d", display:"flex", alignItems:"center", gap:6 }}>✓ All constraints satisfied</div>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                          {violations.map((v, i) => (
                            <div key={i} style={{ padding:"8px 10px", borderRadius:6, background:"rgba(255,46,46,0.07)", border:"1px solid rgba(255,46,46,0.18)" }}>
                              <div style={{ fontSize:9, color:"#ff6b6b", textTransform:"uppercase", letterSpacing:"1px", marginBottom:2 }}>{v.rule}</div>
                              <div style={{ fontSize:11, color:"var(--gray_400)" }}>{v.msg}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Risk preview */}
                    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 20px" }}>
                      <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:12 }}>Risk Preview</div>
                      {[
                        { label:"VaR 99%",    before:BASE_METRICS.varPct,      after:simMetrics.varPct,      unit:"%",  hi:false },
                        { label:"Volatility", before:BASE_METRICS.volatility,  after:simMetrics.volatility,  unit:"%",  hi:false },
                        { label:"Beta",       before:BASE_METRICS.beta,        after:simMetrics.beta,        unit:"",   hi:false },
                        { label:"Sharpe",     before:BASE_METRICS.sharpe,      after:simMetrics.sharpe,      unit:"",   hi:true  },
                      ].map((m, i) => {
                        const delta = m.after - m.before;
                        const good  = m.hi ? delta > 0.005 : delta < -0.005;
                        const bad   = m.hi ? delta < -0.005 : delta > 0.005;
                        const clr   = good ? "#04ff1d" : bad ? "#ff2e2e" : "var(--gray_500)";
                        return (
                          <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                            <span style={{ fontSize:11, color:"var(--gray_500)" }}>{m.label}</span>
                            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <span style={{ fontSize:11, fontFamily:"monospace", color:"var(--gray_500)", textDecoration:"line-through", opacity:0.6 }}>{m.before.toFixed(2)}{m.unit}</span>
                              <span style={{ fontSize:13, fontFamily:"monospace", fontWeight:700, color:clr }}>{m.after.toFixed(2)}{m.unit}</span>
                            </div>
                          </div>
                        );
                      })}
                      <button style={{ ...btn(true), width:"100%", textAlign:"center", marginTop:6 }} onClick={() => setActiveTab("impact")}>
                        View Full Impact →
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Trade list ── */}
                <div style={secLabel}>Trade List</div>
                <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--gray_900)", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)" }}>
                      {trades.length} trade{trades.length !== 1 ? "s" : ""}
                    </span>
                    {totalTradeCost > 0 && (
                      <span style={{ fontSize:12, color:"var(--gray_500)" }}>
                        Est. total cost: <strong style={{ color:"var(--white_a700)", fontFamily:"monospace" }}>${Math.round(totalTradeCost).toLocaleString()}</strong>
                      </span>
                    )}
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:11, color:"var(--gray_500)" }}>Min size</span>
                      <input type="number" step="0.05" min="0" max="5" value={minTradeSize} onChange={e => setMinTradeSize(+e.target.value)}
                        style={{ width:55, padding:"4px 8px", fontSize:12, fontFamily:"monospace", background:"var(--gray_900_02)", border:"1px solid var(--gray_900)", borderRadius:5, color:"var(--white_a700)", outline:"none", textAlign:"right" }} />
                      <span style={{ fontSize:11, color:"var(--gray_500)" }}>%</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:11, color:"var(--gray_500)" }}>Est. cost</span>
                      <input type="number" step="1" min="0" max="100" value={estBps} onChange={e => setEstBps(+e.target.value)}
                        style={{ width:55, padding:"4px 8px", fontSize:12, fontFamily:"monospace", background:"var(--gray_900_02)", border:"1px solid var(--gray_900)", borderRadius:5, color:"var(--white_a700)", outline:"none", textAlign:"right" }} />
                      <span style={{ fontSize:11, color:"var(--gray_500)" }}>bps</span>
                    </div>
                    <button style={{ ...btn(), marginLeft:"auto" }} onClick={() => exportTradesCSV(trades, estBps)}>Export CSV ↓</button>
                  </div>
                  {trades.length === 0 ? (
                    <div style={{ padding:"28px", textAlign:"center", fontSize:13, color:"var(--gray_500)" }}>
                      No trades above minimum size of {minTradeSize}%
                    </div>
                  ) : (
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
                          <th style={th()}>Ticker</th>
                          <th style={th()}>Name</th>
                          <th style={th()}>Action</th>
                          <th style={th(true)}>Current %</th>
                          <th style={th(true)}>Target %</th>
                          <th style={th(true)}>Trade %</th>
                          <th style={th(true)}>Trade Value</th>
                          <th style={th(true)}>Est. Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((t, i) => {
                          const tradeVal = Math.abs(t.diff) * AUM / 100;
                          const cost     = tradeVal * estBps / 10000;
                          return (
                            <tr key={t.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                              <td style={td()}>
                                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <div style={{ width:6, height:6, borderRadius:2, background:CLASS_INFO[t.assetClass]?.color??"#888", flexShrink:0 }} />
                                  <span style={{ fontSize:13, fontWeight:600, fontFamily:"monospace", color:"var(--white_a700)" }}>{t.ticker}</span>
                                </div>
                              </td>
                              <td style={td()}>{t.name}</td>
                              <td style={{ padding:"10px 14px" }}>
                                <span style={{ fontSize:10, padding:"2px 8px", borderRadius:4, fontWeight:700,
                                  background:t.diff>0?"rgba(245,158,11,0.12)":"rgba(4,255,29,0.08)",
                                  color:t.diff>0?"#f59e0b":"#04ff1d" }}>
                                  {t.diff > 0 ? "BUY" : "SELL"}
                                </span>
                              </td>
                              <td style={td(true, true, "var(--gray_400)")}>{t.currentWeight.toFixed(2)}%</td>
                              <td style={td(true, true, "var(--white_a700)")}>{t.targetWeight.toFixed(2)}%</td>
                              <td style={td(true, true, t.diff>0?"#f59e0b":"#04ff1d")}>
                                {t.diff>0?"+":""}{t.diff.toFixed(2)}%
                              </td>
                              <td style={td(true, true, "var(--white_a700)")}>{fmtK(tradeVal)}</td>
                              <td style={td(true, false, "var(--gray_500)")}>~${Math.round(cost).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}

            {/* ═══════════════════════════════════════════════════════════
                TAB 2 — RISK IMPACT
            ═══════════════════════════════════════════════════════════ */}
            {activeTab === "impact" && (
              <>
                {/* Metric delta cards */}
                <div style={secLabel}>Current vs Simulated Metrics</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:28 }}>
                  <MetricDeltaCard label="VaR 1d 99%"   before={BASE_METRICS.varPct}     after={simMetrics.varPct}     unit="%" accentColor="#ff2e2e" i={0} />
                  <MetricDeltaCard label="Volatility"    before={BASE_METRICS.volatility}  after={simMetrics.volatility}  unit="%" accentColor="#f59e0b" i={1} />
                  <MetricDeltaCard label="Beta"          before={BASE_METRICS.beta}        after={simMetrics.beta}        unit=""  accentColor="#a084c9" i={2} />
                  <MetricDeltaCard label="Sharpe Ratio"  before={BASE_METRICS.sharpe}      after={simMetrics.sharpe}      unit=""  accentColor="#04ff1d" i={3} higherIsBetter />
                </div>

                {/* VaR gauges + budget */}
                <div style={{ display:"grid", gridTemplateColumns:"220px 220px 1fr", gap:16, marginBottom:28, alignItems:"start" }}>
                  <VaRGauge utilization={BASE_METRICS.varUtilization} limitPct={BASE_METRICS.varLimit} label="Current VaR Utilization" />
                  <VaRGauge utilization={simMetrics.varUtilization}    limitPct={simMetrics.varLimit}   label="Simulated VaR Utilization" />
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 20px" }}>
                    <div style={{ fontSize:10, color:"var(--gray_500)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:14 }}>VaR Budget</div>
                    {[
                      { label:"Current VaR",        val:`${BASE_METRICS.varPct.toFixed(2)}%  ·  ${fmtK(BASE_METRICS.varPct*AUM/100)}` },
                      { label:"Simulated VaR",       val:`${simMetrics.varPct.toFixed(2)}%  ·  ${fmtK(simMetrics.varPct*AUM/100)}` },
                      { label:"VaR Limit",           val:`${BASE_METRICS.varLimit.toFixed(1)}%  ·  ${fmtK(BASE_METRICS.varLimit*AUM/100)}` },
                      { label:"Budget Remaining",    val:`${Math.max(0, BASE_METRICS.varLimit - simMetrics.varPct).toFixed(2)}%` },
                    ].map((r, i) => (
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", marginBottom:i<3?10:0, paddingBottom:i<3?10:0, borderBottom:i<3?"1px solid rgba(255,255,255,0.05)":"none" }}>
                        <span style={{ fontSize:12, color:"var(--gray_500)" }}>{r.label}</span>
                        <span style={{ fontSize:13, fontFamily:"monospace", fontWeight:600, color:i===1?(simMetrics.varPct > BASE_METRICS.varPct?"#ff2e2e":"#04ff1d"):"var(--white_a700)" }}>{r.val}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Allocation comparison */}
                <div style={secLabel}>Asset Allocation Comparison</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
                  <AllocationDonut data={allocationBefore} title="Current Allocation" />
                  <AllocationDonut data={allocationAfter}  title="Simulated Allocation" />
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
                  {/* Allocation delta chart */}
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 20px" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)", marginBottom:14 }}>Allocation Delta by Asset Class</div>
                    {allocationDelta.length === 0 ? (
                      <div style={{ fontSize:12, color:"var(--gray_500)", textAlign:"center", padding:"32px 0" }}>No allocation changes</div>
                    ) : (
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={allocationDelta} layout="vertical" margin={{ left:8, right:24, top:0, bottom:0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                          <XAxis type="number" tickFormatter={v=>`${v>0?"+":""}${v.toFixed(1)}%`} tick={{ fontSize:10, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize:11, fill:"var(--gray_400)" }} axisLine={false} tickLine={false} width={90} />
                          <Tooltip content={<DeltaTooltip />} />
                          <ReferenceLine x={0} stroke="rgba(255,255,255,0.15)" />
                          <Bar dataKey="delta" name="Δ Weight" radius={[0,4,4,0]}>
                            {allocationDelta.map((d, i) => <Cell key={i} fill={d.delta >= 0 ? "#f59e0b" : "#04ff1d"} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* VaR contribution changes */}
                  <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"18px 20px" }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)", marginBottom:14 }}>VaR Contribution Changes</div>
                    <div style={{ overflowY:"auto", maxHeight:230 }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead>
                          <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
                            <th style={{ ...th(), position:"sticky", top:0, background:"var(--gray_900_01)" }}>Ticker</th>
                            <th style={{ ...th(true), position:"sticky", top:0, background:"var(--gray_900_01)" }}>Before</th>
                            <th style={{ ...th(true), position:"sticky", top:0, background:"var(--gray_900_01)" }}>After</th>
                            <th style={{ ...th(true), position:"sticky", top:0, background:"var(--gray_900_01)" }}>Δ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {varContribChanges.map((v, i) => {
                            const clr = Math.abs(v.delta) < 0.1 ? "var(--gray_500)" : v.delta > 0 ? "#ff2e2e" : "#04ff1d";
                            return (
                              <tr key={v.ticker} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                                <td style={{ padding:"7px 10px" }}>
                                  <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                                    <div style={{ width:5, height:5, borderRadius:1, background:CLASS_INFO[v.assetClass]?.color??"#888", flexShrink:0 }} />
                                    <span style={{ fontSize:12, fontFamily:"monospace", fontWeight:600, color:"var(--white_a700)" }}>{v.ticker}</span>
                                  </div>
                                </td>
                                <td style={{ padding:"7px 10px", textAlign:"right", fontSize:12, fontFamily:"monospace", color:"var(--gray_400)" }}>{v.before.toFixed(1)}%</td>
                                <td style={{ padding:"7px 10px", textAlign:"right", fontSize:12, fontFamily:"monospace", color:"var(--white_a700)" }}>{v.after.toFixed(1)}%</td>
                                <td style={{ padding:"7px 10px", textAlign:"right", fontSize:12, fontFamily:"monospace", fontWeight:600, color:clr }}>
                                  {v.delta >= 0 ? "+" : ""}{v.delta.toFixed(1)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ═══════════════════════════════════════════════════════════
                TAB 3 — SCENARIOS & SENSITIVITY
            ═══════════════════════════════════════════════════════════ */}
            {activeTab === "scenarios" && (
              <>
                {/* Sensitivity analysis */}
                <div style={secLabel}>Sensitivity Analysis</div>
                <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, padding:"20px 24px", marginBottom:28 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20, flexWrap:"wrap" }}>
                    <span style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)" }}>Position</span>
                    <select value={safeIdx} onChange={e => setSensIdx(+e.target.value)} style={{ padding:"6px 12px", fontSize:12, background:"var(--gray_900_02)", border:"1px solid var(--gray_900)", borderRadius:6, color:"var(--white_a700)", outline:"none", cursor:"pointer" }}>
                      {nonLocked.map((p, i) => (
                        <option key={p.ticker} value={i}>{p.ticker} — {p.name}</option>
                      ))}
                    </select>
                    <span style={{ fontSize:11, color:"var(--gray_500)" }}>
                      Current weight: <strong style={{ color:"var(--white_a700)" }}>{positions.find(p=>p.ticker===sensTicker)?.targetWeight.toFixed(1)}%</strong>
                    </span>
                    <span style={{ fontSize:11, color:"var(--gray_500)", marginLeft:"auto" }}>Showing ±12% delta · other positions adjust proportionally</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
                    {[
                      { key:"varPct",     label:"VaR 99%",      color:"#ff2e2e", unit:"%" },
                      { key:"volatility", label:"Volatility",   color:"#f59e0b", unit:"%" },
                      { key:"sharpe",     label:"Sharpe Ratio", color:"#04ff1d", unit:""  },
                    ].map(c => (
                      <div key={c.key} style={{ background:"var(--gray_900_02)", borderRadius:8, padding:"14px 12px" }}>
                        <div style={{ fontSize:11, fontWeight:600, color:"var(--white_a700)", marginBottom:10 }}>{c.label} Sensitivity</div>
                        <ResponsiveContainer width="100%" height={140}>
                          <LineChart data={sensCurve} margin={{ left:-10, right:10, top:5, bottom:5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                            <XAxis dataKey="delta" tickFormatter={v=>`${v>0?"+":""}${v}%`} tick={{ fontSize:9, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize:9, fill:"var(--gray_500)" }} axisLine={false} tickLine={false} tickFormatter={v=>`${v}${c.unit}`} />
                            <Tooltip content={<SensTooltip unit={c.unit} />} />
                            <ReferenceLine x={0} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
                            <Line type="monotone" dataKey={c.key} stroke={c.color} strokeWidth={2} dot={false} activeDot={{ r:4, fill:c.color }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Saved scenarios + comparison */}
                <div style={{ display:"grid", gridTemplateColumns:"290px 1fr", gap:20 }}>

                  {/* Scenario list */}
                  <div>
                    <div style={secLabel}>Saved Scenarios</div>
                    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, overflow:"hidden" }}>
                      {/* Save form */}
                      <div style={{ padding:"14px 16px", borderBottom:"1px solid var(--gray_900)" }}>
                        <div style={{ fontSize:11, color:"var(--gray_500)", marginBottom:8 }}>Save current workbench as:</div>
                        <div style={{ display:"flex", gap:6 }}>
                          <input type="text" placeholder="Scenario name…" value={scenarioName}
                            onChange={e => setScenarioName(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && handleSaveScenario()}
                            style={{ flex:1, padding:"6px 10px", fontSize:12, background:"var(--gray_900_02)", border:"1px solid var(--gray_900)", borderRadius:5, color:"var(--white_a700)", outline:"none" }} />
                          <button style={btn(true)} onClick={handleSaveScenario} disabled={!scenarioName.trim()}>Save</button>
                        </div>
                      </div>
                      {/* Scenario entries */}
                      {savedScenarios.map((s, i) => {
                        const sm = computeMetrics(s.positions);
                        return (
                          <div key={s.id} style={{ padding:"12px 16px", borderBottom:i<savedScenarios.length-1?"1px solid rgba(255,255,255,0.04)":"none" }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                              <div style={{ minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:600, color:"var(--white_a700)", marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</div>
                                <div style={{ fontSize:10, color:"var(--gray_500)" }}>{s.createdAt} · VaR {sm.varPct.toFixed(2)}% · Sharpe {sm.sharpe.toFixed(2)}</div>
                              </div>
                              <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                                <button style={{ ...btn(), padding:"3px 8px", fontSize:11 }} onClick={() => handleLoadScenario(s)}>Load</button>
                                <button style={{ ...btn(false,true), padding:"3px 8px", fontSize:11 }} onClick={() => handleDeleteScenario(s.id)}>✕</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {savedScenarios.length === 0 && (
                        <div style={{ padding:"20px 16px", fontSize:12, color:"var(--gray_500)", textAlign:"center" }}>No saved scenarios</div>
                      )}
                    </div>
                  </div>

                  {/* Comparison table */}
                  <div>
                    <div style={secLabel}>Scenario Comparison</div>
                    <div style={{ background:"var(--gray_900_01)", border:"1px solid var(--gray_900)", borderRadius:10, overflow:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead>
                          <tr style={{ borderBottom:"1px solid var(--gray_900)" }}>
                            <th style={th()}>Metric</th>
                            {scenarioComparison.map((s, i) => (
                              <th key={i} style={{ ...th(true), color:i===0?"var(--gray_500)":i===1?"var(--indigo_a700)":"var(--gray_400)" }}>
                                {s.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { label:"VaR 99%",         key:"varPct",         unit:"%",  lower:true },
                            { label:"VaR Utilization", key:"varUtilization", fmt: v=>`${(v*100).toFixed(1)}%`, lower:true },
                            { label:"Volatility",      key:"volatility",     unit:"%",  lower:true },
                            { label:"Beta",            key:"beta",           unit:"",   lower:true },
                            { label:"Sharpe Ratio",    key:"sharpe",         unit:"",   lower:false },
                          ].map((row, ri) => (
                            <tr key={row.key} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)", background:ri%2===0?"transparent":"rgba(255,255,255,0.015)" }}>
                              <td style={{ padding:"10px 16px", fontSize:12, color:"var(--gray_500)" }}>{row.label}</td>
                              {scenarioComparison.map((s, ci) => {
                                const val  = s.metrics[row.key];
                                const base = scenarioComparison[0].metrics[row.key];
                                const good = row.lower ? val < base - 0.005 : val > base + 0.005;
                                const bad  = row.lower ? val > base + 0.005 : val < base - 0.005;
                                const clr  = ci===0 ? "var(--white_a700)" : good ? "#04ff1d" : bad ? "#ff2e2e" : "var(--gray_400)";
                                const disp = row.fmt ? row.fmt(val) : `${val.toFixed(2)}${row.unit||""}`;
                                return (
                                  <td key={ci} style={{ padding:"10px 16px", textAlign:"right", fontSize:13, fontFamily:"monospace", fontWeight:ci===1?700:400, color:clr }}>
                                    {disp}
                                    {ci>0 && Math.abs(val-base)>0.005 && (
                                      <span style={{ fontSize:10, marginLeft:4, opacity:0.7 }}>{good?"▲":"▼"}</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

        </main>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { opacity: 1; }
        select option { background: #1f2023; }
      `}</style>
    </>
  );
}
